/**
 * Where the blocks of one day go in the hour grid.
 *
 * Split out of SlotGridView.jsx so it can be tested: this is arithmetic with
 * no screen in it, and an arrangement that quietly hides an appointment is
 * exactly the kind of fault nobody sees until a tester sends a photograph.
 * See test/dayLayout.test.mjs.
 */

export const START_HOUR = 6;
export const END_HOUR = 23;

export function eventGeo(ev, slotH) {
  if (ev.allDay) return null;
  const s  = new Date(ev.start);
  const e  = ev.end ? new Date(ev.end) : new Date(+s + 3_600_000);
  const sh = s.getHours() + s.getMinutes() / 60;
  let   eh = e.getHours() + e.getMinutes() / 60;
  // Event ending after midnight (e.g. 22:00 → 00:30): its end hour is numerically
  // BEFORE its start, so it used to compute a negative height and vanish. Clamp
  // the end to the bottom of the grid so the block is still drawn today.
  if (eh <= sh) eh = END_HOUR;
  const cs = Math.max(sh, START_HOUR);
  const ce = Math.min(eh, END_HOUR);
  if (ce <= cs) return null;
  return {
    top:    (cs - START_HOUR) * slotH,
    height: Math.max((ce - cs) * slotH, slotH * 0.4),
  };
}

/**
 * Where every block of one day goes, worked out together instead of one at a
 * time — because "together" is the whole problem.
 *
 * Until now each block was drawn at the full width of the column. Two events
 * at the same hour landed exactly on top of each other and the second one
 * ERASED the first: not unreadable, invisible. On Jodi's Thursday there are
 * four things running at once — Work, Punch in, Wayne work, See Terry? — and
 * her calendar app puts them side by side. MeMo would have shown her one.
 *
 * The arrangement is the ordinary one:
 *   1. blocks are grouped into clusters — runs of events that touch, directly
 *      or through a neighbour;
 *   2. inside a cluster each block takes the first lane whose previous block
 *      has already finished;
 *   3. the cluster's width is split between however many lanes it needed.
 *
 * So a day with no overlaps looks exactly as it did — one lane, full width —
 * and only the hours that genuinely collide get narrower.
 */
export function layoutDay(evs, slotH) {
  const placed = [];
  for (const ev of evs) {
    const g = eventGeo(ev, slotH);
    if (g) placed.push({ id: ev.id, top: g.top, height: g.height, bottom: g.top + g.height, lane: 0 });
  }
  // Earliest first; at the same minute, the longer one takes the left lane,
  // which is where the eye starts.
  placed.sort((a, b) => a.top - b.top || b.height - a.height);

  const out = new Map();
  let i = 0;
  while (i < placed.length) {
    const cluster = [placed[i]];
    let clusterEnd = placed[i].bottom;
    let j = i + 1;
    // Half a pixel of tolerance: an event that ends exactly when the next one
    // starts is not an overlap, and must not cost both of them half the width.
    while (j < placed.length && placed[j].top < clusterEnd - 0.5) {
      cluster.push(placed[j]);
      clusterEnd = Math.max(clusterEnd, placed[j].bottom);
      j++;
    }
    const laneEnds = [];
    for (const b of cluster) {
      let lane = laneEnds.findIndex(end => end <= b.top + 0.5);
      if (lane < 0) { laneEnds.push(b.bottom); lane = laneEnds.length - 1; }
      else laneEnds[lane] = b.bottom;
      b.lane = lane;
    }
    for (const b of cluster) {
      out.set(b.id, { top: b.top, height: b.height, lane: b.lane, lanes: laneEnds.length });
    }
    i = j;
  }
  return out;
}
